import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, usersAPI } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // 1. Initial state synchronization from localStorage
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      // Basic sanity check
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null' && 
          token && token !== 'undefined' && token !== 'null') {
        const parsedUser = JSON.parse(savedUser);
        if (parsedUser && typeof parsedUser === 'object') {
          console.log('[AuthContext] Restoring session for:', parsedUser.name || parsedUser.phone);
          return parsedUser;
        }
      }
    } catch (err) {
      console.warn('[AuthContext] Initial state sync failed:', err.message);
    }
    return null;
  });
  
  const [loading, setLoading] = useState(true);

  // 2. Auth Callbacks (Stable)
  const logout = useCallback(async () => {
    console.log('[AuthContext] Logout initiated');
    try {
      // Notify backend (fire and forget)
      authAPI.logout().catch(e => console.warn('[AuthContext] Remote logout failed', e.message));
    } catch (e) {}
    
    // Clear local storage carefully
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('last_login_time');
    
    // Clear all weighment polling markers to prevent cross-account leaks
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('last_weighment_id')) {
        localStorage.removeItem(key);
      }
    });
    
    setUser(null);
  }, []);

  const login = useCallback(async (phone, password) => {
    console.log('[AuthContext] Login request for:', phone);
    try {
      const response = await authAPI.login({ phone, password });
      const result = response?.data;
      
      if (result?.success) {
        const t = result.token || result.data?.token;
        const u = result.user || result.data?.user || result.data;
        
        if (t) {
          localStorage.setItem('token', t);
          localStorage.setItem('last_login_time', Date.now().toString());
          if (u && typeof u === 'object') {
            localStorage.setItem('user', JSON.stringify(u));
            setUser(u);
          }
          console.log('[AuthContext] Login successful');
        } else {
          console.error('[AuthContext] Login succeeded but no token received');
        }
      }
      return result || { success: false, message: 'Server returned empty response' };
    } catch (err) {
      console.error('[AuthContext] Login error:', err.message);
      return { success: false, message: err.response?.data?.message || 'Network connection failed' };
    }
  }, []);

  const register = useCallback(async (regData) => {
    console.log('[AuthContext] Registration request');
    try {
      const response = await authAPI.register(regData);
      const result = response?.data;
      
      if (result?.success) {
        const t = result.token || result.data?.token;
        const u = result.user || result.data?.user || result.data;
        
        if (t) {
          localStorage.setItem('token', t);
          localStorage.setItem('last_login_time', Date.now().toString());
          if (u && typeof u === 'object') {
            localStorage.setItem('user', JSON.stringify(u));
            setUser(u);
          }
          console.log('[AuthContext] Registration successful');
        }
      }
      return result || { success: false, message: 'Server returned empty response' };
    } catch (err) {
      console.error('[AuthContext] Registration error:', err.message);
      return { success: false, message: err.response?.data?.message || 'Network connection failed' };
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      if (!prev) return null;
      const merged = { ...prev, ...updates };
      try {
        localStorage.setItem('user', JSON.stringify(merged));
      } catch (e) {
        console.warn('[AuthContext] Failed to persist updated user');
      }
      return merged;
    });
  }, []);

  // 3. Side Effects
  
  // Intercept global logout events
  useEffect(() => {
    const onGlobalLogout = () => {
      console.warn('[AuthContext] Global logout triggered');
      logout();
    };
    window.addEventListener('app:logout', onGlobalLogout);
    return () => window.removeEventListener('app:logout', onGlobalLogout);
  }, [logout]);

  // Initial Session Verification
  useEffect(() => {
    let isActive = true;
    const token = localStorage.getItem('token');
    
    if (!token || token === 'undefined' || token === 'null') { 
      setLoading(false);
      return; 
    }
    
    console.log('[AuthContext] Verifying active session...');
    authAPI.verify()
      .then(res => {
        if (!isActive) return;
        
        const success = res?.data?.success;
        const userData = res?.data?.user || res?.data?.data?.user || res?.data?.data;

        if (success && userData && typeof userData === 'object') {
          console.log('[AuthContext] Session verified for:', userData.name);
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } else if (success === false) {
          console.warn('[AuthContext] Session invalid, logging out');
          logout();
        }
      })
      .catch(err => { 
        if (!isActive) return;
        const status = err.response?.status;
        console.error('[AuthContext] Session verification failed:', err.message, status || '');
        
        // Only log out on definitive auth errors (401/403). 
        // For 500 or network errors, we keep the local session.
        if (status === 401 || status === 403) {
          logout();
        }
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => { isActive = false; };
  }, [logout]);

  // Heartbeat every 30s — updating status to 'online'
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(() => {
      // Background heartbeat should NEVER trigger a logout.
      // If it fails, we just log it. The session is managed by the "verify" call and manual actions.
      usersAPI.heartbeat().catch(err => {
        if (err.response?.status === 401) {
           console.warn('[AuthContext] Heartbeat 401 received, but ignoring to prevent auto-logout. Session might be stale but we wait for user action or verify() to fail.');
        }
      });
    }, 30000);
    return () => clearInterval(iv);
  }, [user]);

  // Mark offline on unload
  useEffect(() => {
    if (!user) return;
    const handler = () => {
      // Use sendBeacon if available for more reliable delivery on close
      usersAPI.offline().catch(() => {});
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
